import { ChevronRight } from 'lucide-react';
import { Fragment, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from 'src/shared/lib/utils';

const TOOLTIP_SIDE_OFFSET = 4;

function findScrollParent(element: HTMLElement): HTMLElement | null {
  let parent = element.parentElement;
  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') return parent;
    parent = parent.parentElement;
  }
  return null;
}

/** Same visual styling as WithTooltip / TooltipContent for consistency when Radix tooltip can't be used (e.g. inside Popover). No animation to avoid jumpiness when position updates. */
const tooltipContentClassName =
  'z-[9999] overflow-hidden font-normal rounded-sm !bg-neutral-700 px-2 py-1 text-xs !text-neutral-50 shadow-md max-w-xs select-none normal-case whitespace-normal text-wrap break-words pointer-events-none';

type TagHierarchyProps = {
  hierarchy: string[];
  className?: string;
};

export default function TagHierarchy({ hierarchy, className }: TagHierarchyProps) {
  const ref = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState(false);
  const [hoverState, setHoverState] = useState<{ rect: DOMRect } | null>(null);
  const isHoveringRef = useRef(false);

  useLayoutEffect(() => {
    const container = ref.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const updateOverflow = () => {
      const contentWidth = measure.offsetWidth;
      const containerWidth = container.clientWidth;
      setOverflow(contentWidth > containerWidth);
    };
    updateOverflow();

    const resizeObserver = new ResizeObserver(updateOverflow);
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [hierarchy]);

  // Keep tooltip position in sync when the scroll container scrolls (e.g. dropdown list)
  useLayoutEffect(() => {
    if (!ref.current) return;
    const element = ref.current;
    const scrollParent = findScrollParent(element);

    const updateRect = () => {
      if (ref.current && isHoveringRef.current) {
        setHoverState({ rect: ref.current.getBoundingClientRect() });
      }
    };

    scrollParent?.addEventListener('scroll', updateRect, true);
    return () => scrollParent?.removeEventListener('scroll', updateRect, true);
  }, []);

  const fullLabel = hierarchy.join(' > ');
  const showNativeTitle = overflow && !hoverState;
  const showCustomTooltip = overflow && hoverState;

  return (
    <>
      <div
        ref={ref}
        title={showNativeTitle ? fullLabel : undefined}
        className={cn('relative flex items-center gap-1 truncate w-full max-w-full min-w-0', className)}
        onPointerEnter={() => {
          if (ref.current) {
            isHoveringRef.current = true;
            setHoverState({ rect: ref.current.getBoundingClientRect() });
          }
        }}
        onPointerLeave={() => {
          isHoveringRef.current = false;
          setHoverState(null);
        }}
      >
        <div
          ref={measureRef}
          aria-hidden
          className="invisible absolute left-0 top-0 whitespace-nowrap flex items-center gap-1"
          style={{ pointerEvents: 'none' }}
        >
          {hierarchy.map((tagName, index) => (
            <Fragment key={`${tagName}-${index}`}>
              {index !== 0 && <ChevronRight className="shrink-0" size={12} />}
              <span>{tagName}</span>
            </Fragment>
          ))}
        </div>
        {hierarchy.map((tagName, index) => (
          <Fragment key={`${tagName}-${index}`}>
            {index !== 0 && <ChevronRight className="shrink-0" size={12} />}
            <span className="truncate">{tagName}</span>
          </Fragment>
        ))}
      </div>
      {showCustomTooltip &&
        hoverState &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            role="tooltip"
            className={tooltipContentClassName}
            style={{
              position: 'fixed',
              left: hoverState.rect.left + hoverState.rect.width / 2,
              top: hoverState.rect.top,
              transform: `translate(-50%, calc(-100% - ${TOOLTIP_SIDE_OFFSET}px))`,
            }}
          >
            {fullLabel}
          </div>,
          document.body
        )}
    </>
  );
}
